import { PercentPipe, formatDate, formatNumber } from '@angular/common';
import { Renderer2 } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import {
  AttributeBase,
  CutOffTimesAndFixingSources,
  GroupedPassedItem,
  Leg,
  RfqLegSettings,
  ScheduleName,
  UserSettings
} from '@approot/models';
import {
  DateUtilities,
  Utilities,
  getOppositePosition,
  handleChoicePrice,
  sleep
} from '@approot/utilities';
import {
  completeScheduleData,
  firstDateToUseFn,
  scheduleButtonName
} from '@approot/utilities/schedule-utilities';
import { DEFAULT_DECIMAL_PLACES } from '../leg.component';
import { clearBarrierCustomOnTypeChange } from './barrier-utilities';
import {
  getAllAttributeOptions,
  getAttributeControlFromKey,
  getAttributeControlType,
  removeDateLag
} from './leg-attribute-utilities';
import { calculateTotalNotionalFromSchedule } from './notional-utilities';
export function fieldNumber(field: string): string {
  return field.replace(/\D/g, '');
}

export function optionsChangeHandler(
  form: FormGroup,
  leg: Leg,
  key: string,
  options: string[] | GroupedPassedItem[],
  defaultValue?: string
): void {
  // clears dropDown and populates the dropDowns options
  if (form.get(key)) {
    let control = getAttributeControlFromKey(key, leg.attributesAsFormControls);

    if (control?.options) {
      control.options = options;
    }

    if (defaultValue != null) {
      // set the default of the drop down, if one has been passed in
      form.get(key).setValue(defaultValue);
    }
  }
}

export function isExpired(valueDateTime: string, limit = 60_000): boolean {
  if (valueDateTime) {
    const delta = Date.now() - new Date(valueDateTime).getTime();
    if (delta >= limit) {
      return true;
    }
  }
  return false;
}

export function addTooltipIfExpired(
  selector: string,
  valueDateTime: string,
  leg: Leg,
  extra?: string,
  limit = 60_000
): void {
  const attribute = getAttributeControlFromKey(
    selector,
    leg.attributesAsFormControls
  );

  if (!isExpired(valueDateTime)) {
    attribute.title = '';
    return;
  }
  const text = formatDate(valueDateTime, 'dd MMM yyyy h:mm:ss a', 'en-US');
  attribute.title = (text + ' ' + (extra || '')).trim();
}

export function getColorIfExpired(data: {
  valueDateTime: string;
}): string | null {
  return isExpired(data.valueDateTime) ? Utilities.orangeTextColor : null;
}

export function makeTextCustomColor(
  key: string,
  color: string,
  leg: Leg
): void {
  const attribute = getAttributeControlFromKey(
    key,
    leg.attributesAsFormControls
  );
  if (attribute) {
    attribute.textColor = color;
  }
}

export function blurRounding(key: string, form: FormGroup, leg: Leg): void {
  const ctrl = form.get(key);
  const attribute = getAttributeControlFromKey(
    key,
    leg.attributesAsFormControls
  );
  const isPercentage = attribute.format === 'percentage';
  if (ctrl.value) {
    const cleanValue = Utilities.formattedNumberToNumber(ctrl.value);
    let value =
      Utilities.roundNumberToDp(
        cleanValue,
        attribute.decimalPlaces ?? DEFAULT_DECIMAL_PLACES
      ) || '';
    if (!Utilities.isNotBlank(value)) {
      return;
    }
    if (isPercentage && value.length) {
      value = value + '%';
    }
    const correspondingSide = getOppositePosition(attribute.key);
    if (handleChoicePrice(form, attribute.key, value)) {
      const attribute = getAttributeControlFromKey(
        key,
        leg.attributesAsFormControls
      );
      makeTextCustomColor(correspondingSide, attribute.textColor, leg);
      leg.usingLatestValues[correspondingSide] = false;
    } else {
      ctrl.setValue(value, { emitEvent: false });
    }
  }
}

export function stringOrFormControlToFormControl(
  field: string | FormControl,
  form: FormGroup
): AbstractControl {
  // takes in string/ form control and returns a form control
  return typeof field === 'string' ? form.get(field) : field;
}

export function removePendingFromField(
  form: FormGroup,
  leg: Leg,
  rfqExecutionSettings: RfqLegSettings,
  field: string | FormControl,
  updateFieldOnly = false
) {
  // removes spinner from the an attribute
  const control = stringOrFormControlToFormControl(field, form);

  if (!control) {
    return;
  }

  if (updateFieldOnly) {
    control.updateValueAndValidity({ emitEvent: false });
    return;
  }

  control.enable({ emitEvent: false });

  if (typeof field === 'string') {
    const attribute = getAttributeControlFromKey(
      field,
      leg.attributesAsFormControls
    );
    if (
      attribute &&
      (!rfqExecutionSettings.static ||
        rfqExecutionSettings.directRfq ||
        rfqExecutionSettings.editableSettings.editableFields.includes(field))
    ) {
      attribute.readOnly = attribute.originalReadOnly;
    }
  }
}

export function markFieldAsPending(
  form: FormGroup,
  leg: Leg,
  field: string | FormControl,
  wipeInfo = true
) {
  // adds a spinner to the wanted attribute
  const control = stringOrFormControlToFormControl(field, form);
  if (!control) {
    return;
  }

  control.markAsPending();

  if (typeof field === 'string') {
    const attribute = getAttributeControlFromKey(
      field,
      leg.attributesAsFormControls
    );
    if (attribute) {
      if (wipeInfo) {
        attribute.info = '';
      }
      attribute.readOnly = true;
    }
  }
}

export function setAsRequiredAndEnable(attribute: string, form: FormGroup) {
  if (form.get(attribute)) {
    form.get(attribute).setValidators([Validators.required]);
    form.get(attribute).enable();
  }
}

export function clearValidatorsAndEnableAttribute(
  attribute: string,
  form: FormGroup
) {
  if (form.get(attribute)) {
    form.get(attribute).clearValidators;
    form.get(attribute).enable();
  }
}

export function clearTextColor(
  leg: Leg,
  key: string,
  overrideColor: string = null
) {
  let attributeType = getAttributeControlType(key, leg);
  if (attributeType === 'dropdown' || attributeType === 'multiSelect') {
    // if the attribute is a barrier Type
    makeTextCustomColor(key, overrideColor || Utilities.whiteTextColor, leg);
  } else {
    makeTextCustomColor(key, overrideColor || 'inherit', leg);
  }
}

export function scheduleLegAttributeChange(
  form: FormGroup,
  leg: Leg,
  key: string,
  scheduleName: string,
  scheduleFieldName?: string
): void {
  scheduleFieldName = scheduleFieldName || key;
  // if an attribute that the schedule relies on changes, the schedule needs to be altered
  if (form.get(scheduleName) && form.get(scheduleName).value) {
    clearTextColor(leg, key); // wipe the custom color if its been set
    clearBarrierCustomOnTypeChange(key, form); // handle the barrier if the barrierType changes
    // this.clearBarrierTypeOnBarrierChange(key); // if barrier changes then if barrierType is Custom clear it

    if (form.get(key).valid || !form.get(key).value) {
      let valueToSaveAs =
        form.get(key).value === Utilities.customText ? '' : form.get(key).value;
      if (
        valueToSaveAs &&
        (key === 'notional' ||
          key === 'notional1' ||
          key === 'notional2' ||
          key === 'callNotional' ||
          key === 'putNotional' ||
          key === 'secondNotional' ||
          key === 'notionalShort' ||
          key === 'notionalLong')
      ) {
        // if there is a schedule and the notional changes, then just
        // change the notional amount in of all occurrences in the object
        valueToSaveAs = Utilities.notionalCurrencyRemover(valueToSaveAs) || '';
      }

      if (form.get(scheduleName).value.length) {
        (form.get(scheduleName) as FormArray).controls.forEach(scheduleForm => {
          if (
            (scheduleForm as FormGroup).controls[scheduleFieldName] !==
            undefined
          ) {
            (scheduleForm as FormGroup).controls[scheduleFieldName].setValue(
              valueToSaveAs
            );
          }
          mirrorLegInability(
            form.get(key).disabled,
            (scheduleForm as FormGroup).controls[scheduleFieldName]
          );
        });
      }
    } else if (!form.get(key).valid) {
      // if text is not valid then wipe it
      if (form.get(scheduleName).value.length) {
        (form.get(scheduleName) as FormArray).controls.forEach(scheduleForm => {
          if (
            (scheduleForm as FormGroup).controls[scheduleFieldName] !==
            undefined
          ) {
            (scheduleForm as FormGroup).controls[scheduleFieldName].setValue(
              ''
            );
          }
          mirrorLegInability(
            form.get(key).disabled,
            (scheduleForm as FormGroup).controls[scheduleFieldName]
          );
        });
      }
    }
  }
}

function mirrorLegInability(
  disableField: boolean,
  ctrl: AbstractControl
): void {
  if (disableField) {
    ctrl.disable({ emitEvent: false });
  } else {
    ctrl.enable({ emitEvent: false });
  }
}

export function isDropdownOptionValid(
  attribute: string,
  preset: string | string[],
  leg: Leg
): boolean {
  // returns whether for a given attribute the preset value is a dropdown option
  const options = getAllAttributeOptions(attribute, leg);
  const atributeControlType = getAttributeControlType(attribute, leg);
  if (atributeControlType !== 'multiSelect') {
    return options.includes(preset);
  }
  return (preset as string[]).every(x => options.includes(x));
}

export function setButtonText(
  buttonName: string,
  text: string,
  leg: Leg,
  form: FormGroup
) {
  // this is now separated, as if a trade is loaded, a new schedule shouldn't be created,
  // instead it should just be loaded in.
  let attribute = getAttributeControlFromKey(
    buttonName,
    leg.attributesAsFormControls
  );
  attribute.info = form.get(buttonName).value.length + ' ' + text;
  attribute.imageSrc = 'assets/calendar.png';
}

export function removeLastFixingIcon(renderer: Renderer2, dateInput: Element) {
  // removes the calendar icon of the last fixing
  if (dateInput) {
    const buttonParent = renderer.parentNode(dateInput);
    const button = buttonParent.querySelector('button');
    if (buttonParent && button) {
      renderer.setStyle(buttonParent, 'width', '100%');
      renderer.setStyle(
        renderer.parentNode(renderer.parentNode(buttonParent)),
        'width',
        '100%'
      );
      renderer.setStyle(buttonParent, 'cursor', 'not-allowed');
      renderer.setStyle(button, 'visibility', 'hidden');
      renderer.setProperty(button, 'disabled', true);

      renderer.setStyle(dateInput, 'pointer-events', 'none');
    }
  }
}

export function disableAndWipeAttribute(
  attributeName: string,
  form: FormGroup
): void {
  if (form.get(attributeName)) {
    form.get(attributeName).disable();
    form.get(attributeName).setValue('');
  }
}

export function usingLatestValues(leg: Leg): boolean {
  return (
    leg.usingLatestValues.spot ||
    leg.usingLatestValues.secondSpot ||
    leg.usingLatestValues.forwardPointsAsk ||
    leg.usingLatestValues.forwardPointsBid ||
    leg.usingLatestValues.volBid ||
    leg.usingLatestValues.volAsk ||
    leg.usingLatestValues.vol1Ask ||
    leg.usingLatestValues.vol1Bid ||
    leg.usingLatestValues.vol2Ask ||
    leg.usingLatestValues.vol2Bid
  );
}

export function shouldLegUseWebSocket(leg: Leg): boolean {
  return (
    leg.productType === 'choosertpf' ||
    leg.productType === 'frozentpf' ||
    leg.productType === 'frozenchoosertpf' ||
    leg.productType === 'rainbowtarf' ||
    leg.productType === 'chooserpivottpf' ||
    leg.productType === 'rainbowpivottarf' ||
    leg.productType === 'accumulator' ||
    leg.productType === 'accumulatorspotsettled' ||
    leg.productType.endsWith('strip')
  );
}

export function loadSchedules(
  scheduleName: ScheduleName,
  form: FormGroup,
  leg: Leg,
  rfqLeg: boolean,
  percentPipe: PercentPipe,
  settings: UserSettings,
  fieldNumber: string = ''
) {
  if (form.get(scheduleName) && leg.preSetPosition[scheduleName]) {
    completeScheduleData(
      leg.preSetPosition[scheduleName],
      scheduleName,
      form,
      leg,
      rfqLeg,
      percentPipe,
      settings,
      fieldNumber
    ); // make the schedule form controls
    setButtonText(scheduleName, scheduleButtonName(scheduleName), leg, form); // set up the button to show the fixings
  }
}

export function onScheduleFieldChange(
  form: FormGroup,
  leg: Leg,
  scheduleName: string,
  changedAttributes: string[]
): void {
  const schedule = form.get(scheduleName)?.value;
  changedAttributes.map(field => {
    field = scheduleFieldNameToLegFieldName(field, scheduleName);
    // loop through attributes that have changed and set the text to custom;
    const fieldControl = form.get(field);

    if (field === firstDateToUseFn(form)) {
      let date = '';
      switch (field) {
        case 'firstFixingDate':
          date = 'fixingDate';
          break;
        case 'firstExpirationDate':
          date = 'expiryDate';
          break;
        case 'firstSettlementDate':
          date = 'settlementDate';
          break;
        case 'firstExerciseDate':
          date = 'exerciseDate';
          break;
      }

      if (fieldControl) {
        fieldControl.setValue(
          DateUtilities.dateToPrettyDate(schedule[0][date]),
          {
            emitEvent: false
          }
        );
      }
      return;
    } else if (
      field === 'lastFixingDate' ||
      field === 'lastExpirationDate' ||
      field === 'lastSettlementDate'
    ) {
      let date;

      if (field === 'lastFixingDate') {
        date = 'fixingDate';
      } else if (field === 'lastExpirationDate') {
        date = 'expiryDate';
      } else if (field === 'lastSettlementDate') {
        date = 'settlementDate';
      }
      if (fieldControl) {
        fieldControl.setValue(
          DateUtilities.dateToPrettyDate(schedule[schedule.length - 1][date]),
          { emitEvent: false }
        );
      }
      return;
    } else if (fieldControl) {
      fieldControl.enable({ emitEvent: false });
      fieldControl.setValue(Utilities.customText, { emitEvent: false });
      fieldControl.setErrors(null); // this should be used instead
      makeTextCustomColor(field, Utilities.orangeTextColor, leg);
    }
  });
  calculateTotalNotionalFromSchedule(form, leg);
  // we want the component to acknowledge that there has been a change in the schedule
}

export function setCalculatedField(
  changedField,
  staticField,
  operator
): string {
  let changedF = Utilities.removeCommasConvertToNumber(changedField);
  let staticF = Utilities.removeCommasConvertToNumber(staticField);
  if (operator === '*') {
    return formatNumber(changedF * staticF, 'en-GB', '1.2-2');
  } else {
    return formatNumber(changedF / staticF, 'en-GB', '1.2-2');
  }
}

export function manuallyUpdateFormControlValidity(
  attributeName: string,
  form: FormGroup
): void {
  // In certain cases we want to carryout validation without having valuechanges being activated
  if (form.get(attributeName)) {
    form.get(attributeName).setErrors(
      form.get(attributeName).valid
        ? null
        : {
            invalidEntry: {
              value: form.get(attributeName).value
            }
          }
    );
  }
}

export function setBracketedAttributeInfo(
  attributeName: string,
  spanText: string,
  leg: Leg
): void {
  // adds a lag next to a date
  const attribute = getAttributeControlFromKey(
    attributeName,
    leg.attributesAsFormControls
  );
  if (attribute) {
    attribute.info = '(' + spanText + ')';
  }
}

export function isGettingALatestStrike(leg: Leg): boolean {
  return (
    leg.usingLatestValues.strike ||
    leg.usingLatestValues.strike1 ||
    leg.usingLatestValues.strike2
  );
}

export function clearCustomText(
  form: FormGroup,
  fieldsToSkip?: string[]
): void {
  // if when we are scheduling new fixings, clear the fields that say Custom
  Object.keys(form.controls).forEach(key => {
    if (fieldsToSkip && fieldsToSkip.includes(key)) {
      return;
    }
    clearEachCustomText(key, form);
  });
}

export function clearEachCustomText(
  attributeName: string,
  form: FormGroup
): void {
  if (form.get(attributeName)?.value === Utilities.customText) {
    form.get(attributeName).setValue('');
  }
}

export function payLoadDate(date: string): string {
  let payloadDate = date;

  if (DateUtilities.isDateOfSlashFormat(date)) {
    // onDate change doesn't react fast enough to just use this.form.get()
    payloadDate = DateUtilities.restructureDate(date);
  } else if (
    DateUtilities.isPrettyDate(date) // backend doesn't support 20 mar 2020
  ) {
    payloadDate = DateUtilities.prettyDateToDate(date);
    // the date needs to be of format 2020-10-10 or 25 dec. So if its not convert it to 2020-10-10
  }

  return payloadDate;
}

export function makeAttributeUseCurrentValue(
  leg: Leg,
  fieldName: string,
  changeColor = true
): void {
  if (leg.usingLatestValues[fieldName]) {
    removeDateLag(fieldName, leg);
  }
  leg.usingLatestValues[fieldName] = false;
  if (changeColor) {
    makeTextCustomColor(fieldName, Utilities.orangeTextColor, leg);
  }
}

export function markAllUsingLatestValuesPending(
  leg: Leg,
  form: FormGroup,
  rfqExecutionSettings: RfqLegSettings,
  markAsPending = true
): void {
  Object.keys(leg.usingLatestValues).forEach(key => {
    if (leg.usingLatestValues[key]) {
      markAsPending
        ? markFieldAsPending(form, leg, key, false)
        : removePendingFromField(form, leg, rfqExecutionSettings, key);
    }
  });
}

export function wipeAttributeInfo(elementId: string, leg: Leg): void {
  let attribute = getAttributeControlFromKey(
    elementId,
    leg.attributesAsFormControls
  );
  attribute.info = '';
}

export function setAttributeReadOnly(
  key: string,
  readOnlyStatus: boolean,
  leg: Leg,
  form: FormGroup,
  rfqExecutionSettings: RfqLegSettings
): void {
  const attributeControl = getAttributeControlFromKey(
    key,
    leg.attributesAsFormControls
  );
  if (
    attributeControl &&
    (!form.get(key).disabled || rfqExecutionSettings.static)
  ) {
    attributeControl.readOnly = readOnlyStatus;
  }
}

export function setAllFieldsReadOnlyState(
  leg: Leg,
  form: FormGroup,
  rfqExecutionSettings: RfqLegSettings,
  isReadOnly: boolean
): void {
  const attributes = Object.keys(form.getRawValue());
  const editableSettings = rfqExecutionSettings.editableSettings;
  attributes.forEach(attribute => {
    if (
      editableSettings?.editableFields.includes(attribute) &&
      !(editableSettings?.lockWhileActive.includes(attribute) && isReadOnly)
    ) {
      return;
    }
    if (rfqExecutionSettings.directRfq && attribute === 'delta') {
      return;
    }
    setAttributeReadOnly(
      attribute,
      isReadOnly,
      leg,
      form,
      rfqExecutionSettings
    );
  });
}

export function enableAttribute(attributeName: string, form: FormGroup): void {
  if (form.get(attributeName)) {
    form.get(attributeName).setValue('');
    form.get(attributeName).enable();
  }
}

export function dropDownSideInfoSetter(
  elementId: string,
  spanText: string,
  leg: Leg
): void {
  // the span should only be added, if there is a base currency selected and an option has been selected
  let attribute = getAttributeControlFromKey(
    elementId,
    leg.attributesAsFormControls
  );
  attribute.info = spanText;
}

export function lowerCaseProductType(productType: string): string {
  return productType?.toLowerCase().replace(/\s/g, '');
}

export function setFixingSourcesCutOffsOptions(
  values: string[],
  fieldToUse: string,
  form: FormGroup,
  leg: Leg,
  cutOffTimesAndFixingSources: CutOffTimesAndFixingSources,
  presetsLoaded: boolean
): void {
  if (!values) {
    console.warn('setFixingSourcesCutOffsOptions - missing "values"');
    return;
  }
  const longNames = values
    ? values
        .map(
          code =>
            cutOffTimesAndFixingSources[
              fieldToUse === 'cutOff' ? 'cutOffs' : 'fixingSources'
            ].find(x => x.code === code).longName
        )
        .filter(name => name)
    : [];
  optionsChangeHandler(
    form,
    leg,
    fieldToUse,
    longNames,
    longNames.length ? null : ''
  );

  if (presetsLoaded || !longNames.includes(form.get(fieldToUse).value)) {
    form.get(fieldToUse).setValue(longNames[0] || '', { emitEvent: false });
  }
}

export function updateValidatorFromMarginStyle(
  val: string,
  field: string,
  leg: Leg
): void {
  const attribute = getAttributeControlFromKey(
    field,
    leg.attributesAsFormControls
  );
  if (!attribute) {
    return;
  }
  switch (val) {
    case 'Amount Base':
    case 'Amount Term':
      attribute.validator = ['numericShorthand'];
      break;
    case 'Pips':
      attribute.validator = ['pips'];
      break;
    case 'Percent Base':
    case 'Percent Term':
    case 'Vol Spread':
      attribute.validator = ['percentage'];
      break;
    default:
      break;
  }
}

export function onMarginStyleChange(
  form: FormGroup,
  val: string,
  leg: Leg,
  doNotResetMargin = false
): void {
  ['margin', 'marginAsk', 'marginBid', 'retrocessionFee'].forEach(
    async field => {
      if (form.get('marginStyle').enabled) {
        updateValidatorFromMarginStyle(val, field, leg);
        await sleep(100);

        if (field === 'margin' && doNotResetMargin) {
          return;
        }
        if (
          (field !== 'margin' &&
            field !== 'marginBid' &&
            field !== 'marginAsk') ||
          val
        ) {
          form.get(field)?.setValue('0');
        }
      }
    }
  );
}

function scheduleFieldNameToLegFieldName(
  fieldName: string,
  scheduleName: string
): string {
  switch (fieldName) {
    case 'notional':
      if (scheduleName === 'fixedLegSchedule1') {
        return 'notional1';
      } else if (scheduleName === 'fixedLegSchedule2') {
        return 'notional2';
      } else {
        return 'notional';
      }
    case 'fixedLegRate':
      if (scheduleName === 'fixedLegSchedule1') {
        return 'fixedLegRate1';
      } else if (scheduleName === 'fixedLegSchedule2') {
        return 'fixedLegRate2';
      } else {
        return 'fixedLegRate';
      }
    default:
      return fieldName;
  }
}

export function getDecimalPlaces(
  ctrlName: string,
  attributesAsFormControls: AttributeBase<any>[]
): number {
  return (
    getAttributeControlFromKey(ctrlName, attributesAsFormControls)
      ?.decimalPlaces ?? DEFAULT_DECIMAL_PLACES
  );
}
